import type { Context, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

// -- Types -------------------------------------------------------------------

export type ContextPanelScope = 'component' | 'global' | 'page';

export interface ContextPanelOpenOptions {
  /**
   * The panel body content.
   */
  content: ReactNode;
  /**
   * Skip the slide-out animation on close and snap the page-body
   * reflow simultaneously. For navigation-driven closes (e.g. tab
   * change). Open animations are unaffected.
   *
   * @default false
   */
  exitImmediate?: boolean;
  /**
   * Custom header content. Use the `ContextPanelHeader` component for a
   * consistent look, or provide any ReactNode for full control.
   */
  header: ReactNode;
  /**
   * Whether to apply default padding to the body content.
   *
   * @default true
   */
  padded?: boolean;
  /**
   * When true, the panel cannot be closed by the user. Defaults to false.
   */
  persistent?: boolean;
  /**
   * Override the default panel width for this invocation.
   */
  width?: string;
}

export interface ContextPanelContextValue {
  /**
   * Closes the panel.
   */
  close: () => void;
  /**
   * Whether the panel is currently open.
   */
  isOpen: boolean;
  /**
   * Opens the panel with the given options.
   */
  open: (options: ContextPanelOpenOptions) => void;
  /**
   * The current panel options, or null when closed.
   */
  options: ContextPanelOpenOptions | null;
}

/**
 * Return type when defaults are provided. The `open` function accepts partial
 * options since defaults fill in any gaps.
 */
export interface ContextPanelWithDefaults extends Omit<
  ContextPanelContextValue,
  'open'
> {
  /**
   * Opens the panel. Options are merged with the defaults provided to the
   * hook — explicit values here take precedence.
   */
  open: (options?: Partial<ContextPanelOpenOptions>) => void;
}

// -- Contexts ----------------------------------------------------------------

const GlobalContextPanelContext =
  createContext<ContextPanelContextValue | null>(null);

const PageContextPanelContext = createContext<ContextPanelContextValue | null>(
  null,
);

const ComponentContextPanelContext =
  createContext<ContextPanelContextValue | null>(null);

/**
 * Maps each scope name to its React context object.
 */
export const contextByScope: Record<
  ContextPanelScope,
  Context<ContextPanelContextValue | null>
> = {
  component: ComponentContextPanelContext,
  global: GlobalContextPanelContext,
  page: PageContextPanelContext,
};

// -- Hook --------------------------------------------------------------------

/**
 * Returns the context panel API for the given scope.
 *
 * Optionally accepts default options that are merged into every `open()` call
 * (explicit values in `open()` take precedence). When the defaults include
 * `persistent: true` along with `header` and `content`, the panel opens
 * automatically on mount and closes on unmount.
 *
 * @param scope - The panel scope to target. Defaults to `'component'`.
 * @param defaults - Default options merged into `open()` calls.
 * @throws When called outside a matching `ContextPanelProvider`.
 *
 * @example
 * // Imperative with defaults — width is pre-set for all open() calls:
 * const { open } = useContextPanel('page', { width: '30rem' });
 * open({ header: ..., content: ... }); // width: 30rem unless overridden
 *
 * @example
 * // Declarative persistent — auto-opens on mount, closes on unmount:
 * useContextPanel('page', {
 *   header: <ContextPanel.Header>Recent Activity</ContextPanel.Header>,
 *   content: <ActivityFeed />,
 *   persistent: true,
 * });
 */
export function useContextPanel(
  scope?: ContextPanelScope,
): ContextPanelContextValue;
export function useContextPanel(
  scope: ContextPanelScope,
  defaults: Partial<ContextPanelOpenOptions>,
): ContextPanelWithDefaults;
export function useContextPanel(
  scope: ContextPanelScope = 'component',
  defaults?: Partial<ContextPanelOpenOptions>,
): ContextPanelContextValue | ContextPanelWithDefaults {
  const ScopedContext = contextByScope[scope];
  const value = useContext(ScopedContext);

  if (!value) {
    throw new Error(
      `useContextPanel('${scope}') must be used within a <ContextPanelProvider scope="${scope}">`,
    );
  }

  const openedRef = useRef(false);
  const closeRef = useRef(value.close);
  closeRef.current = value.close;

  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  /**
   * Wrap open/close to merge defaults and track ownership. If this hook
   * instance called open(), the panel is closed on unmount — preventing
   * orphaned panels when the opening component unmounts.
   */
  const wrappedOpen = useCallback(
    (options?: Partial<ContextPanelOpenOptions>) => {
      const merged = {
        ...defaultsRef.current,
        ...options,
      } as ContextPanelOpenOptions;
      openedRef.current = true;
      value.open(merged);
    },
    [value.open],
  );

  const wrappedClose = useCallback(() => {
    openedRef.current = false;
    value.close();
  }, [value.close]);

  // Auto-open on mount when defaults include persistent + header + content
  const shouldAutoOpen =
    !!defaults?.persistent &&
    defaults.header != null &&
    defaults.content != null;

  useEffect(() => {
    if (!shouldAutoOpen) return;
    wrappedOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, [shouldAutoOpen]);

  /**
   * Layout-effect timing so the close commits in the same paint frame
   * as the consumer's unmount; otherwise the panel persists for one
   * frame, visible as a stutter on navigation.
   */
  useIsomorphicLayoutEffect(
    () => () => {
      if (openedRef.current) closeRef.current();
    },
    [],
  );

  return {
    ...value,
    close: wrappedClose,
    open: wrappedOpen,
  };
}
